import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "ag-grid-enterprise";
import type { SsrmRequestBody, SsrmResponseBody } from "../ag_grid_shared/types";

export class ServerSideDatasource implements IServerSideDatasource {
  private readonly gridDataUrl: string;
  private readonly getSearchText: () => string;
  private readonly setPinnedBottomRow: (rows: unknown[]) => void;
  private readonly getExcludeVoteBoxes: () => boolean;
  // Additive, opt-in (voter-list importation-overview Gap A) — mirrors `getSearchText`'s own
  // precedent for a live, controller-owned value read fresh on every request. Defaults to a
  // no-op returning null so every EXISTING caller that doesn't pass a 5th argument is
  // unaffected: `externalFilters` is simply never added to the request body.
  private readonly getExternalFilters: () => Record<string, unknown> | null;

  constructor(
    gridDataUrl: string,
    getSearchText: () => string,
    setPinnedBottomRow: (rows: unknown[]) => void,
    getExcludeVoteBoxes: () => boolean = () => false,
    getExternalFilters: () => Record<string, unknown> | null = () => null
  ) {
    this.gridDataUrl = gridDataUrl;
    this.getSearchText = getSearchText;
    this.setPinnedBottomRow = setPinnedBottomRow;
    this.getExcludeVoteBoxes = getExcludeVoteBoxes;
    this.getExternalFilters = getExternalFilters;
  }

  getRows(params: IServerSideGetRowsParams): void {
    const request = params.request;

    const body: SsrmRequestBody = {
      startRow: request.startRow ?? 0,
      endRow: request.endRow ?? 100,
      sortModel: (request.sortModel ?? []).map((s) => ({
        colId: s.colId,
        sort: s.sort as "asc" | "desc",
      })),
      filterModel: (request.filterModel ?? {}) as SsrmRequestBody["filterModel"],
      rowGroupCols: (request.rowGroupCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        displayName: c.displayName ?? "",
      })),
      groupKeys: request.groupKeys ?? [],
      valueCols: (request.valueCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        aggFunc: c.aggFunc ?? "",
      })),
      pivotCols: (request.pivotCols ?? []).map((c) => ({
        id: c.id,
        field: c.field ?? "",
        displayName: c.displayName ?? "",
      })),
      pivotMode: request.pivotMode ?? false,
      searchText: this.getSearchText(),
      ...(this.getExcludeVoteBoxes() ? { excludeVoteBoxes: true } : {}),
    };

    const externalFilters = this.getExternalFilters();
    if (externalFilters && Object.keys(externalFilters).length > 0) {
      body.externalFilters = externalFilters;
    }

    const csrfToken =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

    fetch(this.gridDataUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`SSRM block request failed: ${response.status}`);
        }
        return response.json() as Promise<SsrmResponseBody>;
      })
      .then((data) => {
        params.success({
          rowData: data.rows,
          rowCount: data.lastRow,
        });

        this.setPinnedBottomRow(data.footerRow ?? []);
      })
      .catch((error) => {
        console.error("[ServerSideDatasource] getRows error:", error);
        params.fail();
      });
  }
}
